import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import profileImage from '../assets/images/Main.png';

const About = () => {
  const skills = [
    {
      category: 'Backend',
      items: ['Python', 'Go', 'PySpark', 'Node.js']
    },
    {
      category: 'Cloud & DevOps',
      items: ['AWS', 'GCP', 'Docker', 'Terraform', 'CI/CD', 'Databricks']
    },
    {
      category: 'Databases',
      items: ['SQL', 'MongoDB']
    },
    {
      category: 'AI Tools & Productivity',
      items: ['Cursor AI', 'GitHub Copilot', 'Claude', 'Gemini']
    }
  ];

  const experience = [
    {
      company: 'GlobalLogic',
      position: 'Associate Consultant',
      period: 'Feb 2025 - Present',
      description: 'Working extensively on Python and AWS technologies, focusing on ETL platform development and data processing solutions.',
      achievements: [
        'Developing advanced ETL pipelines using Python and AWS services',
        'Working on Rivery - a comprehensive cloud-based data integration platform',
        'Building scalable data ingestion and transformation solutions',
        'Leveraging AI tools (Cursor, Copilot, Claude) for enhanced productivity and code quality'
      ]
    },
    {
      company: 'Incedo Inc.',
      position: 'Senior Software Engineer',
      period: 'Apr 2022 - Jan 2025',
      description: 'Led development of AI-powered solutions and cloud-native applications with expertise in AWS and modern technologies.',
      achievements: [
        'Developed Python-based solutions for data extraction achieving 95% cost reduction',
        'Architected cloud services on AWS with 400% processing speed improvement',
        'Engineered ML models and implemented robust CI/CD pipelines',
        'Worked on projects like Bizzy Libretto, PIMDT, and System of Insights'
      ]
    },
    {
      company: 'Incedo Inc.',
      position: 'Software Engineer',
      period: 'Sep 2019 - Apr 2022',
      description: 'Developed scalable software solutions and gained expertise in cloud technologies.',
      achievements: [
        'Built modern web applications using cutting-edge technologies',
        'Collaborated with cross-functional teams on enterprise projects',
        'Contributed to system design and architecture decisions',
        'Gained expertise in AWS cloud services and modern development practices'
      ]
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Profile Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img
                  src={profileImage}
                  alt="Mayank Sharma"
                  className="w-full h-auto object-contain"
                />
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-primary-200 rounded-full opacity-50 blur-xl"></div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-accent-200 rounded-full opacity-50 blur-xl"></div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:pl-8"
            >
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                <span className="gradient-text">Mayank Sharma</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Associate Consultant with a passion for building 
                scalable data solutions and ETL platforms that make a real impact.
              </p>
              
        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
          Based in Gurugram, Haryana, India, with over 6 years of experience in software development, data engineering, 
          and cloud architecture, I've helped companies scale from startups to enterprise solutions, 
          always focusing on clean code, best practices, and robust data solutions.
        </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary">
                  See My Work
                </Link>
                <Link to="/contact" className="btn-secondary">
                  Contact Me
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Technical <span className="gradient-text">Skills</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              I work with modern technologies and frameworks to build robust,
              scalable data engineering solutions, ETL platforms, and cloud infrastructure.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {skills.map((skillGroup, index) => (
              <motion.div
                key={skillGroup.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 rounded-xl p-6 card-hover"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {skillGroup.category}
                </h3>
                <div className="space-y-2">
                  {skillGroup.items.map((skill) => (
                    <div
                      key={skill}
                      className="px-3 py-1 bg-white rounded-full text-sm text-gray-700 shadow-sm"
                    >
                      {skill}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Professional <span className="gradient-text">Experience</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              My journey through different roles and companies, building 
              expertise in software engineering and cloud technologies.
            </p>
          </motion.div>

          <div className="space-y-8">
            {experience.map((exp, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg card-hover"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {exp.position}
                    </h3>
                    <p className="text-primary-600 font-semibold mb-2">
                      {exp.company}
                    </p>
                    <p className="text-gray-500 text-sm">{exp.period}</p>
                  </div>
                  
                  <div className="lg:col-span-2">
                    <p className="text-gray-600 mb-4">{exp.description}</p>
                    <ul className="space-y-2">
                      {exp.achievements.map((achievement, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-accent-500 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{achievement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Project Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Current <span className="gradient-text">Project</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Currently working on Rivery, a comprehensive cloud-based data integration platform
            </p>
          </motion.div>

          <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Rivery - Advanced Data Integration Platform
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Working on a comprehensive SaaS platform that makes it easy to build end-to-end ELT data pipelines. 
                  Rivery enables organizations to ingest, transform, orchestrate, and activate data from any source 
                  to any destination with no-code or custom code approaches.
                </p>
                
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Key Features I'm Working On:</h4>
                  <ul className="space-y-2">
                    {[
                      'Advanced data ingestion from 200+ sources',
                      'Real-time data transformation using Python and SQL',
                      'Automated data orchestration and workflow management',
                      'Cloud-native architecture on AWS and other platforms',
                      'Reverse ETL for operational data activation'
                    ].map((feature, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="flex items-center space-x-3"
                      >
                        <div className="w-2 h-2 bg-accent-500 rounded-full"></div>
                        <span className="text-gray-700">{feature}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-xl p-8 shadow-lg"
              >
                <h4 className="text-xl font-semibold text-gray-900 mb-6">Technologies & Impact</h4>
                <div className="space-y-4">
                  <div>
                    <h5 className="font-medium text-gray-900 mb-2">Tech Stack</h5>
                    <div className="flex flex-wrap gap-2">
                      {['Python', 'AWS', 'ETL/ELT', 'Data Pipelines', 'Cloud Architecture'].map((tech) => (
                        <span key={tech} className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-900 mb-2">Key Achievements</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 7.5x faster time to value for data delivery</li>
                      <li>• 33% reduction in data-related costs</li>
                      <li>• 50% less time spent on data processing</li>
                    </ul>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Work Together?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              I'm always interested in discussing new opportunities and 
              innovative projects. Let's create something amazing together.
            </p>
            <Link
              to="/contact"
              className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg inline-flex items-center space-x-2"
            >
              <span>Get In Touch</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
